import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, useCollectionBinding, useContentToken } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Weidner override of the vantage AmenitiesList. Copied from the parent theme
 * (the parent does not export this slot for a style wrapper). The only change
 * from the base is larger image carousels on mobile: the card image switches
 * from a 16:9 letterbox to taller 4:3 / 3:2 ratios at small breakpoints so the
 * photos read clearly on phones. Keep in sync with parent for other changes.
 */

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

// Words kept lowercase when title-casing amenity labels.
const LOWERCASE_WORDS = new Set(['and']);

// Capitalize each word of an amenity label except small words like "and".
// Only the first letter is changed so acronyms (TV, BBQ, EV) keep their casing.
function titleCaseAmenity(text: string): string {
  return text.replace(/\S+/g, (word) => {
    if (LOWERCASE_WORDS.has(word.toLowerCase())) return word.toLowerCase();
    // Capitalize the first letter of the word and the first letter after any
    // dash so "In-unit" becomes "In-Unit". Matches every dash variant the data
    // might use (ASCII hyphen, non-breaking hyphen U+2011, en/em dashes, minus)
    // and preserves the original separator character.
    return word.replace(
      /(^|[-‐-―−])(\p{L})/gu,
      (_match, sep: string, letter: string) => sep + letter.toUpperCase()
    );
  });
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>

          <div className="amenities-slider-dots">
            {photos.map((_, i) => (
              <button
                key={i}
                className={`amenities-slider-dot${i === current ? ' amenities-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Paginated amenities list — fixed page sizes keep card height predictable.
// ============================================================================

const ITEMS_PER_PAGE_DESKTOP = 8;
const ITEMS_PER_PAGE_MOBILE = 6;
const MOBILE_BREAKPOINT = 640;

function useItemsPerPage() {
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DESKTOP);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const sync = () => {
      setItemsPerPage(mq.matches ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE_DESKTOP);
    };
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return itemsPerPage;
}

interface PaginatedAmenitiesListProps {
  items: Amenity[];
}

function PaginatedAmenitiesList({ items }: PaginatedAmenitiesListProps) {
  const itemsPerPage = useItemsPerPage();
  const [page, setPage] = useState(0);
  const [pageAnimKey, setPageAnimKey] = useState(0);
  const [slideDir, setSlideDir] = useState<'next' | 'prev'>('next');

  useEffect(() => {
    setPage(0);
    setPageAnimKey(k => k + 1);
  }, [items.length, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));
  const safePage = Math.min(page, totalPages - 1);
  const currentItems = items.slice(
    safePage * itemsPerPage,
    (safePage + 1) * itemsPerPage
  );
  const showPagination = totalPages > 1;

  const goToPage = useCallback((nextPage: number) => {
    setSlideDir(nextPage > safePage ? 'next' : 'prev');
    setPage(nextPage);
    setPageAnimKey(k => k + 1);
  }, [safePage]);

  const paddedItems: (Amenity | null)[] = [...currentItems];
  while (paddedItems.length < itemsPerPage) {
    paddedItems.push(null);
  }

  return (
    <div className="amenities-list-area">
      <div
        className="amenities-list-area-inner"
        style={{ minHeight: `calc(${itemsPerPage} * var(--amenities-item-row-height))` }}
      >
        <ul
          key={pageAnimKey}
          className={`amenities-col-list amenities-col-list--page amenities-col-list--page-${slideDir}`}
        >
          {paddedItems.map((item, i) => (
            <li
              key={item ? `p${safePage}-${i}` : `pad-${safePage}-${i}`}
              className={`amenities-col-item${item ? '' : ' amenities-col-item--placeholder'}`}
              aria-hidden={!item}
            >
              <span className="amenities-col-bullet" aria-hidden="true" />
              <span>{item?.title ? titleCaseAmenity(item.title) : '\u00A0'}</span>
            </li>
          ))}
        </ul>
      </div>

      <div
        className={`amenities-pagination${showPagination ? '' : ' amenities-pagination--hidden'}`}
        aria-hidden={!showPagination}
      >
        <button
          type="button"
          className="amenities-pagination-btn"
          onClick={() => goToPage(Math.max(0, safePage - 1))}
          disabled={!showPagination || safePage === 0}
          aria-label="Previous amenities page"
          tabIndex={showPagination ? 0 : -1}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
        </button>
        <span className="amenities-pagination-info">
          {safePage + 1} / {totalPages}
        </span>
        <button
          type="button"
          className="amenities-pagination-btn"
          onClick={() => goToPage(Math.min(totalPages - 1, safePage + 1))}
          disabled={!showPagination || safePage === totalPages - 1}
          aria-label="Next amenities page"
          tabIndex={showPagination ? 0 : -1}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Column component
// ============================================================================

interface AmenityCardProps {
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
}

function AmenityCard({ headingNode, headingText, items, photos }: AmenityCardProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const hasImage = photos.length > 0;

  return (
    <div ref={colRef} className="reveal-fade-up amenities-card">
      {hasImage && (
        <div className="amenities-card-image">
          <PhotoSlider photos={photos} heading={headingText} />
        </div>
      )}
      <div className="amenities-card-body">
        {headingNode}
        <PaginatedAmenitiesList items={items} />
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function AmenitiesList() {
  const communityHeading = useContentToken('amenitieslist.communityHeading');
  const apartmentHeading = useContentToken('amenitieslist.apartmentHeading');

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;
  const columnCount =
    (communityItems.length > 0 ? 1 : 0) + (apartmentItems.length > 0 ? 1 : 0);

  return (
    <section className="py-8 md:py-12 bg-[var(--vibe-color-background)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {hasContent ? (
          <div
            className="amenities-grid"
            data-columns={columnCount}
          >
            {communityItems.length > 0 && (
              <AmenityCard
                headingNode={
                  <EditableToken
                    path="amenitieslist.communityHeading"
                    as="h3"
                    className="amenities-col-heading"
                  >
                    {communityHeading || 'Community Amenities'}
                  </EditableToken>
                }
                headingText={communityHeading || 'Community Amenities'}
                items={communityItems}
                photos={communityPhotos}
              />
            )}
            {apartmentItems.length > 0 && (
              <AmenityCard
                headingNode={
                  <EditableToken
                    path="amenitieslist.apartmentHeading"
                    as="h3"
                    className="amenities-col-heading"
                  >
                    {apartmentHeading || 'Interior Features'}
                  </EditableToken>
                }
                headingText={apartmentHeading || 'Interior Features'}
                items={apartmentItems}
                photos={apartmentPhotos}
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        /* Two-column grid of bordered amenity cards */
        .amenities-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          align-items: stretch;
        }
        .amenities-grid[data-columns="1"] {
          grid-template-columns: 1fr;
          max-width: 48rem;
          margin: 0 auto;
        }

        @media (max-width: 768px) {
          .amenities-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          .amenities-grid[data-columns="1"] {
            max-width: none;
          }
          /* Larger image carousels on mobile: in the single-column layout the
             card spans the full width, so a taller ratio gives the photos more
             room than the desktop 16:9 letterbox. */
          .amenities-card-image {
            aspect-ratio: 3 / 2;
          }
        }

        /* Bordered card: image on top, heading, list */
        .amenities-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          border: 3px solid var(--vibe-color-primary);
          background: white;
          padding: 1.25rem;
        }

        .amenities-card-image {
          position: relative;
          overflow: hidden;
          flex-shrink: 0;
          width: 100%;
          aspect-ratio: 16 / 9;
          margin-bottom: 1.25rem;
        }
        .amenities-card-image .amenities-slider {
          position: absolute;
          inset: 0;
          height: 100%;
        }

        .amenities-card-body {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }
        .amenities-card-body .amenities-list-area {
          flex: 1;
        }

        @media (max-width: 900px) {
          .amenities-card {
            padding: 1rem;
          }
          .amenities-card-image {
            margin-bottom: 1rem;
          }
        }

        @media (max-width: 640px) {
          .amenities-card {
            padding: 0.875rem;
            border-width: 2.5px;
          }
          /* Even larger on small phones. */
          .amenities-card-image {
            aspect-ratio: 4 / 3;
          }
        }

        /* Photo slider */
        .amenities-slider {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          flex-shrink: 0;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.55);
        }
        .amenities-slider-arrow--prev { left: 10px; }
        .amenities-slider-arrow--next { right: 10px; }

        .amenities-slider-dots {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 6px;
          z-index: 2;
        }
        .amenities-slider-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .amenities-slider-dot:hover {
          transform: scale(1.25);
          border-color: white;
        }
        .amenities-slider-dot--active {
          background: white;
          border-color: white;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        .amenities-col-heading {
          font-family: var(--vibe-font-heading);
          font-size: 1.35rem;
          font-weight: 700;
          color: var(--vibe-color-text);
          text-align: left;
          text-transform: uppercase;
          padding: 0 0 0.75rem;
          margin: 0 0 0.75rem;
          letter-spacing: 0.06em;
          line-height: 1.25;
          position: relative;
        }
        .amenities-col-heading::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 2.5rem;
          height: 3px;
          background: var(--vibe-color-primary);
        }

        @media (max-width: 900px) {
          .amenities-col-heading {
            font-size: 1.15rem;
            padding-bottom: 0.6rem;
          }
        }
        @media (max-width: 640px) {
          .amenities-col-heading::after {
            height: 2.5px;
          }
        }
        @media (max-width: 600px) {
          .amenities-col-heading {
            font-size: 1rem;
            letter-spacing: 0.04em;
          }
        }

        /* List + bottom pagination */
        .amenities-list-area {
          --amenities-item-row-height: 1.9rem;
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }
        @media (max-width: 640px) {
          .amenities-list-area {
            --amenities-item-row-height: 1.85rem;
          }
        }
        .amenities-list-area-inner {
          flex-shrink: 0;
          overflow: hidden;
        }

        .amenities-col-list {
          list-style: none;
          padding: 0;
          margin: 0;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 0;
          text-align: left;
        }
        .amenities-col-list--page-next {
          animation: amenities-page-next 0.38s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-col-list--page-prev {
          animation: amenities-page-prev 0.38s cubic-bezier(0.22, 1, 0.36, 1);
        }
        @keyframes amenities-page-next {
          from {
            opacity: 0;
            transform: translateX(18px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes amenities-page-prev {
          from {
            opacity: 0;
            transform: translateX(-18px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @media (max-width: 900px) {
          .amenities-col-list {
            padding: 0;
          }
        }
        @media (max-width: 640px) {
          .amenities-col-list {
            padding: 0;
          }
        }

        .amenities-col-item {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
          min-height: var(--amenities-item-row-height);
          padding: 0.15rem 0;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          color: var(--vibe-color-text);
          line-height: 1.5;
          text-align: left;
        }
        .amenities-col-item > span:last-child {
          flex: 1;
          min-width: 0;
        }
        .amenities-col-item--placeholder {
          visibility: hidden;
          pointer-events: none;
        }

        @media (max-width: 900px) {
          .amenities-col-item {
            font-size: 0.9375rem;
            gap: 0.5rem;
          }
        }
        @media (max-width: 640px) {
          .amenities-col-item {
            font-size: 0.875rem;
            line-height: 1.45;
          }
        }

        .amenities-col-bullet {
          flex-shrink: 0;
          width: 4px;
          height: 4px;
          border-radius: 50%;
          background: var(--vibe-color-text);
          margin-top: 0.55em;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-col-list--page-next,
          .amenities-col-list--page-prev {
            animation: none;
          }
        }

        .amenities-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          flex-shrink: 0;
          height: 4.25rem;
          padding: 0 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          color: var(--vibe-color-text);
        }
        .amenities-pagination--hidden {
          visibility: hidden;
          pointer-events: none;
        }
        @media (max-width: 640px) {
          .amenities-pagination {
            gap: 0.65rem;
            height: 4.5rem;
            font-size: 0.8125rem;
          }
        }
        .amenities-pagination-btn {
          width: 36px;
          height: 36px;
          padding: 0;
          border-radius: 50%;
          border: 2px solid var(--vibe-color-text);
          background: white;
          color: var(--vibe-color-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s, transform 0.15s;
        }
        .amenities-pagination-btn:hover:not(:disabled) {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: white;
        }
        .amenities-pagination-btn:active:not(:disabled) {
          transform: scale(0.94);
        }
        .amenities-pagination-btn:disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }
        @media (max-width: 640px) {
          .amenities-pagination-btn {
            width: 44px;
            height: 44px;
          }
        }
        .amenities-pagination-info {
          min-width: 3.25rem;
          text-align: center;
          font-variant-numeric: tabular-nums;
          font-weight: 600;
          color: var(--vibe-color-text);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'text',
      defaultValue: 'Interior Features',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
